import React from 'react';
import { Modal, Rate, Card, Divider } from 'antd';
import Title from 'antd/es/typography/Title';
import PropTypes from 'prop-types';
import { FlexWrapper, GreyText, GridBox } from '../../../theme/common_style';
import styled from '@emotion/styled';
import dayjs from 'dayjs';
import { generateEmployeeImgUrl, getFullName } from '../../../utils/common_functions';
import { AvatarGroupRow } from '../../../components/common/AvatarGroup';
import { useResponsiveModalWidth } from '../../../hooks/useResponsiveModalWidth';

const FeedbackCard = styled(Card)`
  margin-bottom: 16px;
  border-radius: 8px;

  .ant-card-body {
    padding: 20px;
    @media (max-width: 600px) {
      padding: 10px !important;
    }
  }
`;

export const RatingSection = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-top: 10px;

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

export const RatingItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  .rating-label {
    font-weight: 500;
    color: #262626;
    font-size: 14px;
  }
`;

const PersonInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background-color: #fafafa;
  border-radius: 8px;

  .person-details {
    display: flex;
    flex-direction: column;

    .name {
      font-weight: 500;
      color: #262626;
      margin-bottom: 2px;
    }

    .role {
      color: #8c8c8c;
      font-size: 12px;
    }
  }
`;

const NoteSection = styled.div`
  margin-top: 20px;

  .note-label {
    font-weight: 500;
    color: #262626;
    margin-bottom: 8px;
    display: block;
  }

  .note-content {
    background-color: #fafafa;
    padding: 16px;
    border-radius: 8px;
    border: 1px solid #f0f0f0;
    color: #595959;
    line-height: 1.6;
    white-space: pre-wrap;
    word-wrap: break-word;
  }
`;

const ViewFeedback = ({ open, onCancel, data }) => {
  const {
    employee,
    communication = 0,
    technical = 0,
    behaviour = 0,
    responsibility = 0,
    note = 'No note provided',
    created_at,
    created_by
  } = data;
  const width = useResponsiveModalWidth(700);
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return dayjs(dateString).format('DD MMM, YYYY');
  };

  return (
    <Modal
      open={open}
      maskClosable={false}
      onCancel={onCancel}
      centered
      prefixCls="antCustomModal"
      destroyOnClose
      width={width}
      footer={false}>
      <Title level={4} style={{ margin: '0 0 20px 0', textAlign: 'center' }}>
        View Feedback
      </Title>

      <FeedbackCard>
        <FlexWrapper justify="start" margin="0 0 16px">
          <GreyText>Date:</GreyText>
          <span style={{ marginLeft: '8px', fontWeight: 500 }}>{formatDate(created_at)}</span>
        </FlexWrapper>

        <GridBox cols={2} gap="8px">
          <div>
            <GreyText style={{ marginBottom: '8px', fontWeight: 500 }}>Feedback Given By:</GreyText>
            <PersonInfo>
              <AvatarGroupRow
                name={
                  created_by?.name ||
                  getFullName(
                    created_by?.first_name,
                    created_by?.middle_name,
                    created_by?.last_name
                  )
                }
                baseUrl={created_by?.name || generateEmployeeImgUrl(created_by?.id)}
              />
              <div className="person-details">
                <span className="name">
                  {created_by?.name ||
                    getFullName(
                      created_by?.first_name,
                      created_by?.middle_name,
                      created_by?.last_name
                    )}
                </span>
              </div>
            </PersonInfo>
          </div>

          <div>
            <GreyText style={{ marginBottom: '8px', fontWeight: 500 }}>Feedback Given To:</GreyText>
            <PersonInfo>
              <AvatarGroupRow
                key={employee?.id}
                name={getFullName(employee?.first_name, employee?.middle_name, employee?.last_name)}
                baseUrl={generateEmployeeImgUrl(employee?.id)}
              />

              <div className="person-details">
                <span className="name">
                  {getFullName(employee?.first_name, employee?.middle_name, employee?.last_name)}
                </span>
                <span className="role"></span>
              </div>
            </PersonInfo>
          </div>
        </GridBox>

        <Divider />

        <div>
          <Title level={5} style={{ margin: '0' }}>
            Performance Ratings
          </Title>
          <RatingSection>
            <RatingItem>
              <span className="rating-label">Communication</span>
              <Rate disabled value={communication} allowHalf />
              <GreyText style={{ fontSize: '12px' }}>{communication}/5</GreyText>
            </RatingItem>

            <RatingItem>
              <span className="rating-label">Technical Skills</span>
              <Rate disabled value={technical} allowHalf />
              <GreyText style={{ fontSize: '12px' }}>{technical}/5</GreyText>
            </RatingItem>

            <RatingItem>
              <span className="rating-label">Behaviour</span>
              <Rate disabled value={behaviour} allowHalf />
              <GreyText style={{ fontSize: '12px' }}>{behaviour}/5</GreyText>
            </RatingItem>

            <RatingItem>
              <span className="rating-label">Responsibility</span>
              <Rate disabled value={responsibility} allowHalf />
              <GreyText style={{ fontSize: '12px' }}>{responsibility}/5</GreyText>
            </RatingItem>
          </RatingSection>
        </div>

        <Divider />

        <NoteSection>
          <span className="note-label">Additional Notes:</span>
          <div className="note-content">{note || 'No additional notes provided.'}</div>
        </NoteSection>
      </FeedbackCard>
    </Modal>
  );
};

export default ViewFeedback;

ViewFeedback.propTypes = {
  open: PropTypes.bool,
  onCancel: PropTypes.func,
  data: PropTypes.any
};
